import { useQuery } from '@tanstack/preact-query';
import { useContext } from 'preact/hooks';
import type {
  Direction,
  LineColors,
  Timetable,
  TimetableVisit,
  TimetableNotice,
} from '../arcticapi/types';

export type { TimetableNotice };
import { ApiClientContext } from '../arcticapi';
// types
import type { TimetableDetailsLine } from './timetableDetailsLine';
import type { VisitRestriction } from '../arcticapi/types';

export interface TimetableTableRow {
  id: string;
  label: string;
  major: boolean;
  indicator?: string | null;
  localityName?: string | null;
  stopAtcoCode?: string | null;
}

export interface TimetableWaypointCell {
  displayTime: string;
  restriction: VisitRestriction | undefined;
}

export interface TimetableJourney {
  id: string;
  lineName: string;
  lineColors?: LineColors;
  // Dense array aligned to TimetableTable.waypoints.
  // The index is the timetable waypoint index, not the order of journey.visits.
  waypointCells: TimetableWaypointCell[];
  noticeCodes?: string[];
}

export interface TimetableTable {
  waypoints: TimetableTableRow[];
  journeys: TimetableJourney[];
  direction: Direction | null;
  switchLink: string | null;
  notices?: TimetableNotice[];
}

export interface UseTimetableState {
  loading: boolean;
  error: Error | null;
  timetable: TimetableTable | null;
  unavailableMessage: string | null;
}

export function useTimetable(
  selectedLine: TimetableDetailsLine | null,
  timetableHrefOverride: string | null = null,
): UseTimetableState {
  const client = useContext(ApiClientContext);
  const timetableHref =
    timetableHrefOverride ?? selectedLine?.timetableHref ?? null;

  const timetableQuery = useQuery<Timetable, Error>({
    queryKey: [
      'line-timetable',
      selectedLine?.id,
      timetableHrefOverride,
      timetableHref,
    ],
    enabled: Boolean(client) && Boolean(timetableHref),
    placeholderData: (previousTimetable, previousQuery) => {
      const previousLineId = previousQuery?.queryKey?.[1];

      return previousLineId === selectedLine?.id
        ? previousTimetable
        : undefined;
    },
    queryFn: ({ signal }) => {
      if (!client || !timetableHref) {
        throw new Error('Timetable request is missing a timetable link.');
      }

      return client.getTimetable(timetableHref, { signal });
    },
  });

  return {
    loading: Boolean(client) && timetableQuery.isFetching,
    error: timetableQuery.error,
    timetable: timetableQuery.data
      ? mapTimetableToTable(
          timetableQuery.data,
          selectedLine?.name ?? null,
          selectedLine?.colors,
        )
      : null,
    unavailableMessage:
      selectedLine && !timetableHref
        ? 'Timetable data is unavailable for this service.'
        : null,
  };
}

function mapTimetableToTable(
  timetable: Timetable,
  fallbackLineName: string | null,
  fallbackLineColors: TimetableDetailsLine['colors'],
): TimetableTable {
  const waypoints = timetable.waypoints.map((waypoint, index) => ({
    id: `waypoint-${index}`,
    label: waypoint.stop.commonName || `Stop ${index + 1}`,
    major: waypoint.major,
    indicator: waypoint.stop.indicator ?? null,
    localityName: waypoint.stop.localityName ?? null,
    stopAtcoCode: waypoint.stop.atcoCode,
  }));

  const journeys = timetable.journeys.map((journey) => {
    const waypointCells = Array.from({ length: waypoints.length }, () =>
      createEmptyWaypointCell(),
    );

    for (const visit of journey.visits) {
      if (visit.waypointIndex >= 0 && visit.waypointIndex < waypoints.length) {
        waypointCells[visit.waypointIndex] = mapVisitToWaypointCell(visit);
      }
    }

    return {
      id: journey.id,
      lineName:
        journey.line?.name ||
        fallbackLineName ||
        `Journey ${journey.sequenceNumber}`,
      lineColors: journey.line?.colors ?? fallbackLineColors,
      waypointCells,
      noticeCodes: journey.noticeCodes ?? [],
    };
  });

  return {
    waypoints,
    journeys,
    direction: timetable.direction ?? null,
    switchLink: timetable.switchLink ?? null,
    notices: timetable.notices ?? [],
  };
}

function createEmptyWaypointCell(): TimetableWaypointCell {
  return {
    displayTime: '--',
    restriction: undefined,
  };
}

function mapVisitToWaypointCell(visit: TimetableVisit): TimetableWaypointCell {
  return {
    displayTime: formatVisitTime(
      visit.aimedDepartureTime ?? visit.aimedArrivalTime,
    ),
    restriction: visit.restriction,
  };
}

function formatVisitTime(value: string | null): string {
  if (!value) return '--';

  if (/^\d{1,2}:\d{2}$/.test(value)) {
    return value;
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return new Intl.DateTimeFormat(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(parsed);
}
